<template>
  <div class="mt-4 px-5">
    <v-alert dense text shaped type="info">
      <b>Arbeitsstunden erfassen</b>
      <div>
        Hier können Sie Ihre geleisteten Arbeitsstunden erfassen. Wählen Sie dazu den Verein, die Tätigkeit und den Arbeitseinsatz aus.
      </div>
    </v-alert>

    <v-card outlined class="mb-4">
      <v-form ref="form_workinghours" v-on:submit.prevent>
        <v-card-text>
          <v-container fluid class="pa-2">
            <v-row>
              <v-col cols="12">
                <v-select
                  v-model="entry.verein"
                  :items="userClubs"
                  item-text="name"
                  item-value="id"
                  label="Verein"
                  outlined
                  dense
                  :rules="[rules.required]"
                  @change="verein_select"
                ></v-select>
              </v-col>

              <v-col cols="12">
                <v-select
                  v-model="entry.selectedFunction"
                  :items="availableFunctions"
                  item-text="name"
                  item-value="id"
                  label="Tätigkeit"
                  outlined
                  dense
                  :rules="[rules.required]"
                  :disabled="!entry.verein"
                  no-data-text="Keine Tätigkeiten für diesen Verein konfiguriert."
                ></v-select>
              </v-col>

              <v-col cols="12" v-if="hasArbeitsstundenFull">
                <v-select
                  v-model="entry.selectedAssignment"
                  :items="availableAssignments"
                  item-text="displayText"
                  item-value="id"
                  label="Arbeitseinsatz (Optional)"
                  outlined
                  dense
                  :disabled="!entry.verein"
                  no-data-text="Keine Arbeitseinsätze gefunden."
                  @change="onAssignmentChange"
                ></v-select>
              </v-col>

              <v-col cols="12" sm="6">
                <v-datetime-picker
                  label="Beginn"
                  date-format="dd.MM.yyyy"
                  time-format="HH:mm"
                  v-model="entry.beginn"
                  @input="validateForm"
                  :text-field-props="{...textFieldProps, rules: [rules.required, rules.timeWindow]}"
                  :time-picker-props="timePickerProps"
                >
                  <template slot="dateIcon">
                    <v-icon>mdi-calendar</v-icon>
                  </template>
                  <template slot="timeIcon">
                    <v-icon>mdi-clock-outline</v-icon>
                  </template>
                </v-datetime-picker>
              </v-col>

              <v-col cols="12" sm="6">
                <v-datetime-picker
                  label="Ende"
                  date-format="dd.MM.yyyy"
                  time-format="HH:mm"
                  v-model="entry.ende"
                  @input="validateForm"
                  :text-field-props="textFieldProps"
                  :time-picker-props="timePickerProps"
                >
                  <template slot="dateIcon">
                    <v-icon>mdi-calendar</v-icon>
                  </template>
                  <template slot="timeIcon">
                    <v-icon>mdi-clock-outline</v-icon>
                  </template>
                </v-datetime-picker>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-card-actions class="pb-4 px-4">
          <v-btn
            text
            to="/user/arbeitsstunden"
            color="grey darken-1"
          >
            <v-icon left>mdi-format-list-bulleted</v-icon>
            Zur Übersicht
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            :loading="loading"
            @click="save"
            large
          >
            <v-icon left>mdi-content-save</v-icon>
            Eintrag speichern
          </v-btn>
        </v-card-actions>
      </v-form>
    </v-card>
  </div>
</template>

<script>
export default {
  name: "RecordWorkingHours",
  data() {
    return {
      loading: false,
      userClubs: [],
      availableFunctions: [],
      availableAssignments: [],
      entry: {
        verein: null,
        selectedFunction: null,
        selectedAssignment: null,
        beginn: new Date(),
        ende: new Date(new Date().getTime() + 60 * 60 * 1000) // Default 1 Std später
      },
      textFieldProps: {
        outlined: true,
        dense: true,
        rules: [v => !!v || "Pflichtfeld."]
      },
      timePickerProps: {
        format: "24hr"
      },
      rules: {
        required: v => !!v || "Pflichtfeld.",
        timeWindow: () => {
          if (!this.entry.selectedAssignment || !this.entry.beginn) return true;
          const assignment = this.availableAssignments.find(a => a.id === this.entry.selectedAssignment);
          if (!assignment) return true;
          
          const begin = this.entry.beginn;

          let startStr = assignment.windowStart.replace(' ', 'T');
          if (!startStr.endsWith('Z')) startStr += 'Z';
          let endStr = assignment.windowEnd.replace(' ', 'T');
          if (!endStr.endsWith('Z')) endStr += 'Z';

          const start = new Date(startStr);
          const end = new Date(endStr);

          if (begin < start || begin > end) {
            return 'Muss zwischen ' + this.formatDateTime(assignment.windowStart) + ' und ' + this.formatDateTime(assignment.windowEnd) + ' liegen.';
          }
          return true;
        }
      }
    };
  },
  computed: {
    hasArbeitsstundenFull() {
      if (!this.entry.verein) return false;
      const clubs = (this.$store.state.UserAuth.user && this.$store.state.UserAuth.user.clubs) || [];
      const appClubs = (this.$store.state.UserApp && this.$store.state.UserApp.Clubs && this.$store.state.UserApp.Clubs.meineVereine) || [];
      const allClubs = clubs.length > 0 ? clubs : appClubs;
      const currentClub = allClubs.find(c => c.id === this.entry.verein);
      return currentClub && ((currentClub.can && currentClub.can.arbeitsstunden_full) || currentClub.has_arbeitsstunden_full);
    }
  },
  async mounted() {
    await this.fetchClubs();
  },
  methods: {
    async fetchClubs() {
      if (this.$store.state.UserApp.Clubs.length === 0 || !this.$store.state.UserApp.Clubs.Vereine) {
        await this.$store.dispatch("UserApp/getClubs");
      }
      
      const clubsData = this.$store.state.UserApp.Clubs;
      // Nur Vereine mit Arbeitsstunden-Berechtigung anzeigen (meineVereine enthält oft mehr Infos)
      const allClubs = clubsData.meineVereine || clubsData.Vereine || [];
      this.userClubs = allClubs.filter(c => (c.can && c.can.arbeitsstunden) || c.has_arbeitsstunden || c.hasArbeitsstundenAccess);

      // Falls keine Vereine mit hasArbeitsstundenAccess gefunden wurden, aber der User in Vereinen ist,
      // nehmen wir zur Sicherheit alle (falls das Flag im User-Response fehlt).
      if (this.userClubs.length === 0 && allClubs.length > 0) {
        this.userClubs = allClubs;
      }

      // Default Verein setzen
      if (clubsData.defVerein && clubsData.defVerein.length > 0) {
        this.entry.verein = clubsData.defVerein[0].defaultVerein;
        if (this.entry.verein) {
          this.verein_select();
        }
      }
    },
    async verein_select() {
      if (!this.entry.verein) return;

      try {
        await this.$store.dispatch("UserApp/getArbeitsstundenFunctions", { club_id: this.entry.verein });
        await this.$store.dispatch("UserApp/getUserArbeitseinsaetze", { club_id: this.entry.verein });

        // Tätigkeiten aus dem Store beziehen
        let functions = [...this.$store.state.UserApp.ArbeitsstundenFunctions];
        const arbeitsdienst = functions.find(f => f.name.toLowerCase().includes("arbeitsdienst"));
        if (arbeitsdienst && functions.length > 1) {
           functions = [arbeitsdienst];
           this.entry.selectedFunction = arbeitsdienst.id;
        } else if (functions.length > 0) {
           this.entry.selectedFunction = functions[0].id;
        }
        this.availableFunctions = functions;

        // Arbeitseinsätze aus dem Store beziehen und aufbereiten
        let assignments = [...this.$store.state.UserApp.Arbeitseinsaetze];
        assignments = assignments.map(a => {
          return {
            ...a,
            displayText: `${a.titel} (${this.formatDateTime(a.geplant_von)})`,
            windowStart: a.geplant_von,
            windowEnd: a.geplant_bis
          };
        });
        
        assignments.sort((a, b) => new Date(a.geplant_von) - new Date(b.geplant_von));
        this.availableAssignments = assignments;
      } catch (error) {
        console.error("Fehler beim Laden der Arbeitsstunden-Daten:", error);
      }
    },
    formatDateTime(dateTimeStr) {
      if (!dateTimeStr) return "";
      let isoStr = dateTimeStr.replace(" ", "T");
      if (!isoStr.endsWith("Z")) isoStr += "Z";
      const date = new Date(isoStr);
      return date.toLocaleString([], {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    validateForm() {
      this.$nextTick(() => {
        if (this.$refs.form_workinghours) {
          this.$refs.form_workinghours.validate();
        }
      });
    },
    onAssignmentChange() {
      this.validateForm();
    },
    async save() {
      if (!this.$refs.form_workinghours.validate()) {
        return;
      }

      this.loading = true;
      try {
        const payload = {
          inputVereinID: this.entry.verein,
          inputSA: 4, // 4 = Arbeitsstunden/Sonstiges im Backend-Mapping
          inputBegin: this.entry.beginn,
          inputEnde: this.entry.ende,
          inputShootingFunction: this.entry.selectedFunction,
          inputArbeitseinsatz: this.entry.selectedAssignment
        };

        await this.$store.dispatch("UserApp/setNewShootingBookRecord", payload);

        // Formular zurücksetzen (außer Verein)
        this.entry.selectedFunction = null;
        this.entry.selectedAssignment = null;
        this.entry.beginn = new Date();
        this.entry.ende = new Date(new Date().getTime() + 60 * 60 * 1000);
        this.$refs.form_workinghours.resetValidation();

      } catch (error) {
        console.error("Fehler beim Speichern:", error);
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>
